import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { get, put, API_URL } from '../../utils/api';

// const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const SystemSettings = () => {
  const { hasRole } = useAuth();
  const [settings, setSettings] = useState({
    accessTokenExpiry: '',
    refreshTokenExpiry: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch JWT settings
        const response = await get(`${API_URL}/auth/settings/jwt`);
        setSettings(response.data);
      } catch (err) {
        // console.error('Error fetching settings:', err);
        setError('Failed to load settings. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if user has permission
    if (hasRole('ADMIN') || hasRole('MANAGEMENT')) {
      fetchSettings();
    } else {
      setError('You do not have permission to access this feature.');
      setLoading(false);
    }
  }, [hasRole]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage('');
      
      // Update JWT settings
      await put(`${API_URL}/auth/settings/jwt`, settings);
      
      setSuccessMessage('Settings updated successfully.');
    } catch (err) {
      // console.error('Error updating settings:', err);
      setError(`Failed to update settings: ${err.response?.data?.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h2 className="text-xl font-bold mb-2">System Settings</h2>
        <p className="text-sm text-gray-600">
          Configure system-wide settings for authentication and security.
        </p>
      </div>
      
      {successMessage && (
        <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6" role="alert">
          <p>{successMessage}</p>
        </div>
      )}
      
      <div className="bg-white border rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-medium mb-4">JWT Token Settings</h3>
        
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="accessTokenExpiry" className="block text-sm font-medium text-gray-700 mb-1">
                Access Token Expiry (in minutes)
              </label>
              <input
                type="number"
                id="accessTokenExpiry"
                name="accessTokenExpiry"
                value={settings.accessTokenExpiry}
                onChange={handleChange}
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={!hasRole('ADMIN')}
              />
              <p className="mt-1 text-sm text-gray-500">
                How long access tokens are valid before requiring refresh.
              </p>
            </div>
            
            <div>
              <label htmlFor="refreshTokenExpiry" className="block text-sm font-medium text-gray-700 mb-1">
                Refresh Token Expiry (in days)
              </label>
              <input
                type="number"
                id="refreshTokenExpiry"
                name="refreshTokenExpiry"
                value={settings.refreshTokenExpiry}
                onChange={handleChange}
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={!hasRole('ADMIN')}
              />
              <p className="mt-1 text-sm text-gray-500">
                How long refresh tokens are valid before requiring re-authentication.
              </p>
            </div>
            
            {hasRole('ADMIN') && (
              <div className="pt-4">
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-opacity-50 disabled:opacity-50"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            )}
          </div>
        </form>
      </div>
      
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Note about token expiry</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                Setting shorter token expiry times improves security but requires users to log in more frequently.
                Finding the right balance is important for both security and user experience.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemSettings; 